import { createI18n } from "vue-i18n";
import { type Locale, localeMap } from "@/locales";
import { convertLanguageCode } from "@/libs";

// import en from "@/locales/en.json";
// import zh_CN from "@/locales/zh_CN.json";

export { type Locale };

type Messages = Partial<Record<Locale, { [key: string]: string }>>;
let i18n: ReturnType<typeof createI18n>;
const LANGUAGE_CACHE_KEY = "userLanguage";

// 从缓存读取语言
function getLanguageFromCache(): Locale | null {
  try {
    const cachedLang = localStorage.getItem(LANGUAGE_CACHE_KEY);
    if (cachedLang) {
      const allLanguages = Object.keys(localeMap) as Locale[];
      if (allLanguages.includes(cachedLang as Locale)) {
        return cachedLang as Locale;
      }
    }
  } catch (error) {
    console.error("Failed to read language from cache:", error);
  }
  return null;
}

export function useI18n() {
  if (!i18n) {
    const messages: Messages = {};
    i18n = createI18n({
      locale: "en", // 默认语言
      fallbackLocale: "en",
      messages: messages,
      silentTranslationWarn: true,
    });

    // 初始化时优先读取缓存语言，其次使用浏览器语言，最后使用默认语言
    const cachedLang = getLanguageFromCache();
    if (cachedLang) {
      loadLanguageAsync(cachedLang);
    } else {
      const browserLocales = navigator.languages.map(convertLanguageCode) as Locale[];
      const allLanguages = Object.keys(localeMap) as Locale[];
      const matchedLocale = browserLocales.find((locale) =>
        allLanguages.includes(locale),
      );
      if (matchedLocale) {
        loadLanguageAsync(matchedLocale);
      } else {
        loadLanguageAsync("en");
      }
    }
  }

  async function loadLanguageAsync(locale: Locale): Promise<void> {
    if (!i18n.global.availableLocales.includes(locale)) {
      try {
        const messages = await import(`@/locales/${locale}.json`);
        i18n.global.setLocaleMessage(locale, messages.default);
      } catch (error) {
        console.error(`Could not load messages for locale ${locale}:`, error);
      }
    }
    document.documentElement.lang = locale;
    i18n.global.locale = locale;
  }

  return { i18n, localeMap, loadLanguageAsync };
}
